/**
 * Events
 *
 * Shorthand for creating smart event listeners.
 *
 * Example: Single event.
 * events.on(document.body, 'click', (event) => {...});
 *
 * Example: Multiple events.
 * events.on(document.body, {
 *    click: (event) = {...},
 *    mouseover: (event) = {...},
 *    ...
 * });
 *
 * Example: Event with condition
 * events.on(this.triggers, {
 *    click: {
 *        condition: () => { // this function should return true/false
 *            return (document.body.clientWidth > 700);
 *        },
 *        callback: (event) => {
 *            event.preventDefault();
 *            !this.isOpen && this.open();
 *        }
 *    }
 * });
 * You can attach a condition to each event type separately.
 * Condition is checked on the event call, providing dynamic handling.
 *
 * NOTE: Please use full name "event" instead of "e" in event listener callback.
 * This makes it easier to find the event object during debugging.
 */

import arrays from "./arrays";

function transformCallback(initialCallback) {
  const isConditional = initialCallback.constructor === Object;
  const condition = isConditional ? initialCallback.condition : true;
  const callback = isConditional ? initialCallback.callback : initialCallback;

  return { condition, callback };
}

function bindEvents(selectors, events, callbackObj) {
  const selectorsArray = arrays.fromNodes(selectors);
  let eventsArray = events;

  if (!(eventsArray instanceof Array)) {
    eventsArray = [eventsArray];
  }

  /* Check for condition on each callback */
  let { callback } = callbackObj;

  /* eslint-disable no-prototype-builtins */
  if (callbackObj.hasOwnProperty("condition") && callbackObj.condition.constructor === Function) {
    callback = (event) => {
      // eslint-disable-next-line no-unused-expressions
      callbackObj.condition() && callbackObj.callback(event);
    };
  }
  /* eslint-enable */

  return eventsArray.map((event) => {
    return selectorsArray.forEach((selector) => selector.addEventListener(event, callback));
  });
}

export default {
  /* Add event listeners to the selector(s) */
  /* eslint-disable no-unsafe-finally */
  on(...args /* selectors, events, callback // selectors, { event: callback } */) {
    try {
      const selectors = args[0];
      const events = args[1];

      /* Multiple events for single selector */
      if (events.constructor === Object) {
        Object.keys(events).forEach((eventName) => {
          const initialCallback = events[eventName];
          const callbackObj = transformCallback(initialCallback);

          bindEvents(selectors, eventName, callbackObj);
        });
      } else {
        /* Single event for single selector */
        const initialCallback = args[2];
        const callbackObj = transformCallback(initialCallback);

        bindEvents(events, selectors, callbackObj);
      }
    } catch (error) {
      throw new Error(error);
    }
    return true;
  },
  /* eslint-enable */

  /* Completely unmount event listener from the selector */
  unmount(selectors, event, func, useCapture = false) {
    const selectorsArray = arrays.fromNodes(selectors);

    /* eslint-disable no-unsafe-finally */
    try {
      selectorsArray.forEach((selector) => selector.removeEventListener(event, func, useCapture));
    } catch (error) {
      throw new Error(error);
    }
    return true;

    /* eslint-enable */
  },

  /* Helper for removing focus from active element */
  loseFocus() {
    // eslint-disable-next-line no-unused-expressions
    document.activeElement && (document.activeElement as HTMLElement).blur();
    document.body.focus();
  },

  /**
   * Trigger custom event on initialized component.
   * Used mainly in component Tabs when changing active content, some
   * things need to be recalculated (content is changing visibility).
   *
   * First we check if the 'element' contains the component(s) at all.
   * Or optionally, pass direct 'currentElement' when you want to
   * trigger event on specific container.
   *
   * If components are present we must check if the components are
   * ready to accept custom event.
   * If everything is ready, custom event is triggered.
   *
   * @param  {HTML element} element Parent container of JS component
   * @param  {HTML element} currentElement Optional: Passed element is the JS component
   * @param  {string} componentClassName Identifier of JS component
   * @param  {string|array} readyClassName Classname or array of classnames which should be present
   * @param  {string} eventId Custom event to trigger when component is initialized
   * @param  {Object} payload Optional data
   */
  triggerCustom({
    element,
    currentElement,
    componentClassName,
    readyClassName,
    eventId,
    payload = {},
  }) {
    const containers = arrays.fromNodes(
      currentElement || element.getElementsByClassName(componentClassName)
    );
    if (!containers.length) return;

    /**
     * @param {HTML element} container
     */
    const checkContainer = (container) => {
      /* Check if component is initialized and ready to accept custom events */
      let componentInitialized;
      const checkComponentState = () => {
        let isReady = true;

        /**
         * Check if all required classnames are present. Stop checking
         * when first fail is encoutered.
         */
        if (Array.isArray(readyClassName)) {
          for (let i = 0; i < readyClassName.length; i += 1) {
            if (!container.classList.contains(readyClassName[i])) {
              isReady = false;
              break;
            }
          }
        } else {
          /* Check if just single classname is required */
          isReady = container.classList.contains(readyClassName);
        }

        /* Component is initialized and ready to accept custom events */
        if (isReady) {
          /* Trigger custom event */
          const event = new CustomEvent(eventId, { detail: payload });
          container.dispatchEvent(event);

          /* Interval not needed anymore */
          clearInterval(componentInitialized);
        }
      };

      componentInitialized = setInterval(checkComponentState, 300);
    };

    containers.forEach(checkContainer);
  },
};
